import React, { useEffect, useState } from 'react';
// You may need to install papaparse: npm install papaparse
import Papa from 'papaparse';
import { ResearchItem } from '../types/research';

const METADATA_URL = '/data/research/metadata.csv';

const CATEGORY_COLUMNS = [
    'AI/ML', 'AI for Healthcare', 'Causal Inference', 'Algorithmic Fairness', 'Graphs'
];

function isTrueCell(v?: string) {
    if (!v) return false;
    const s = String(v).trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'y';
}

type ResearchRow = ResearchItem & Record<string, any>;

const ResearchCards: React.FC = () => {
    const [rows, setRows] = useState<ResearchRow[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeCategory, setActiveCategory] = useState<string>('All');

    useEffect(() => {
        fetch(METADATA_URL)
            .then(res => { if (!res.ok) throw new Error('Failed to load research metadata'); return res.text(); })
            .then(csvText => {
                const parsed = Papa.parse<ResearchItem>(csvText, { header: true });
                const data = (parsed.data as any[]).filter(r => r && Object.keys(r).length > 0) as ResearchItem[];
                // convert category columns to booleans on each row
                const processed: ResearchRow[] = data.map(r => {
                    const copy: ResearchRow = { ...r };
                    CATEGORY_COLUMNS.forEach(c => { copy[c] = isTrueCell(r[c]); });
                    return copy;
                });
                setRows(processed);
                if (parsed.meta && Array.isArray(parsed.meta.fields)) {
                    setHeaders(parsed.meta.fields as string[]);
                } else if (data.length > 0) {
                    // fallback: derive headers from first row
                    setHeaders(Object.keys(data[0]));
                }
                setLoading(false);
            })
            .catch(err => { setError(String(err)); setLoading(false); });
    }, []);

    // Staggered fade-in animation when filter changes
    useEffect(() => {
        const grid = document.querySelector('.research-grid') as HTMLElement | null;
        if (!grid) return;
        const items = Array.from(grid.querySelectorAll('.research-card')) as HTMLElement[];
        items.forEach((el) => {
            el.style.opacity = '0';
            el.style.animation = 'none';
        });
        // force reflow
        void grid.offsetHeight;
        items.forEach((el, i) => {
            el.style.animation = `fadeStagger 320ms ease-out forwards`;
            el.style.animationDelay = `${i * 60}ms`;
        });
    }, [activeCategory]);

    // renderValue helper: returns a React node for common value types (strings, arrays, URLs)
    const renderValue = (val: any): React.ReactNode => {
        if (val === null || val === undefined) return null;
        if (Array.isArray(val)) {
            return val.map((v, i) => <span key={i}>{String(v)}{i < val.length - 1 ? ', ' : ''}</span>);
        }
        if (typeof val === 'string') {
            const text = val.trim();
            // If value looks like a URL, render as a link
            if (/^https?:\/\//i.test(text)) {
                // open links in same tab (do not spawn a new window)
                return <a href={text}>{text}</a>;
            }
            // If pipe-separated or semicolon-separated list, split and join
            if (text.includes('|')) {
                return text.split('|').map((s, i) => <span key={i}>{s.trim()}{i < text.split('|').length - 1 ? ', ' : ''}</span>);
            }
            if (text.includes(';')) {
                return text.split(';').map((s, i) => <span key={i}>{s.trim()}{i < text.split(';').length - 1 ? ', ' : ''}</span>);
            }
            return text;
        }
        // fallback for numbers / booleans / objects
        if (typeof val === 'object') {
            try {
                return JSON.stringify(val);
            } catch {
                return String(val);
            }
        }
        return String(val);
    };

    if (loading) return <div></div>;
    if (error) return <div>Error: {error}</div>;

    const categories = headers.filter(h => CATEGORY_COLUMNS.includes(h));
    const filteredRows = (activeCategory === 'All' ? rows : rows.filter(r => !!(r as any)[activeCategory]))
        .sort((a, b) => {
            // Sort by year, latest first (descending)
            const yearA = parseInt(a['Year'] || '0', 10);
            const yearB = parseInt(b['Year'] || '0', 10);
            return yearB - yearA;
        });

    // columns we don't want to render in the generic field list because
    // they are displayed specially or should be hidden. Compare lower-case
    // to avoid casing mismatches in CSV headers.
    const HIDDEN_FIELDS = new Set([
        'id', 'name', 'name 2', 'github', 'url', 'url 2', 'slides', 'type', 'icon', 'description',
        // user-requested hidden fields
        'pages', 'pages 2', 'edition', 'edition 2', 'month', 'month 2',
        // hide journal/year since we'll render them combined
        'journal', 'year', 'journal 2', 'year 2',
        // hide paper title and author list since we render them in order
        'paper title', 'paper title 2', 'author list', 'author list 2'
    ]);

    function normalizeHref(v?: string) {
        if (!v) return '';
        const s = String(v).trim();
        if (!s) return '';
        if (/^https?:\/\//i.test(s)) return s;
        return `https://${s}`;
    }

    // Helper to read a field from the row using multiple possible header names
    function getField(row: ResearchRow, candidates: string[]) {
        for (const name of candidates) {
            const v = (row as any)[name];
            if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
            const lower = (row as any)[name.toLowerCase()];
            if (lower !== undefined && lower !== null && String(lower).trim() !== '') return String(lower).trim();
            const upper = (row as any)[name.toUpperCase()];
            if (upper !== undefined && upper !== null && String(upper).trim() !== '') return String(upper).trim();
        }
        return '';
    }

    // Render an author list string, highlighting "Daphna Keidar" in bold.
    function renderAuthorsWithHighlight(raw?: string): React.ReactNode {
        if (!raw) return null;
        const text = String(raw).trim();
        if (!text) return null;
        // split on common separators: comma, semicolon, pipe, ' and ', ' & ', '/'
        const parts = text.split(/\s*(?:\||;|,| and | & |\/)\s*/i).map(p => p.trim()).filter(Boolean);
        return parts.map((p, i) => {
            // match exact "Daphna Keidar" optionally followed by asterisks
            const m = p.match(/^(Daphna Keidar)(\*+)?$/);
            const isMe = !!m;
            return (
                <span key={i}>
                    {isMe ? <strong>{m![1]}</strong> : p}
                    {/* render trailing asterisks (if any) but not bolded */}
                    {isMe && m![2] ? m![2] : null}
                    {i < parts.length - 1 ? ', ' : ''}
                </span>
            );
        });
    }

    // (removed unused aggregate checks that triggered lint warnings)

    return (
        <div>
            {/* Category filter buttons */}
            <div className="gallery-filter research-filter">
                <button onClick={() => setActiveCategory('All')} className={`filter-btn${activeCategory === 'All' ? ' active' : ''}`}>All</button>
                {categories.map(cat => (
                    <button key={cat} onClick={() => setActiveCategory(cat)} className={`filter-btn${activeCategory === cat ? ' active' : ''}`}>{cat}</button>
                ))}
            </div>

            <div className={`research-grid${filteredRows.length === 2 ? ' two-only' : ''}`}>
                {filteredRows.map((row, idx) => (
                    <article key={row.ID || idx} className="research-card">
                        {/* Gradient header with Name */}
                        <div style={{
                            background: '#FFFFFF',
                            padding: '1rem 1.2rem',
                            margin: '-0.8rem -0.8rem 0.8rem -0.8rem',
                            borderRadius: '12px 12px 0 0'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1.3em', fontWeight: 600, lineHeight: 1.3, color: '#333' }}>
                                {row['Name'] || row['Title'] || row['Project'] || `Research ${idx + 1}`}
                            </h3>
                            {/* Secondary Name if it exists */}
                            {(() => {
                                const name2 = getField(row, ['Name 2', 'Name2', 'Title 2', 'Project 2']);
                                if (name2) {
                                    return (
                                        <div style={{ marginTop: '0.5rem', fontSize: '1em', fontWeight: 500, lineHeight: 1.3, color: '#555' }}>
                                            {name2}
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        {/* Top row: Image and Title/Description */}
                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                            {/* Image */}
                            <div style={{ flexShrink: 0, width: '80px' }}>
                                {row['Icon'] ? (
                                    <img
                                        src={`/data/research/images/square500x500/${row['Icon']}.png`}
                                        alt={row['Name'] || 'Project'}
                                        width={80}
                                        height={80}
                                        loading="lazy"
                                        decoding="async"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            maxHeight: '80px',
                                            objectFit: 'contain',
                                            borderRadius: '8px',
                                            display: 'block',
                                            background: '#f3f3f3'
                                        }}
                                        onError={(e) => {
                                            // try a smaller thumbnail path first (if generated), otherwise hide
                                            const id = String(row['Icon']);
                                            const thumb1 = `/data/research/images/thumbnails/${id}.jpg`;
                                            const imgEl = e.currentTarget as HTMLImageElement;
                                            const trySrc = (src: string | null) => {
                                                if (!src) return false;
                                                const tester = new Image();
                                                tester.onload = () => { imgEl.src = src; };
                                                tester.onerror = () => { /* noop */ };
                                                tester.src = src;
                                                return true;
                                            };
                                            // attempt thumbnails; if none load, hide the element
                                            trySrc(thumb1) || (() => { imgEl.style.display = 'none'; const fallback = imgEl.nextElementSibling as HTMLElement; if (fallback) fallback.style.display = 'block'; })();
                                        }}
                                    />
                                ) : null}
                                {/* Fallback red placeholder when no icon or image fails to load */}
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    background: '#DAF7FE',
                                    borderRadius: '8px',
                                    display: row['Icon'] ? 'none' : 'block'
                                }}>
                                </div>
                            </div>

                            {/* Title and Description column */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Paper Title */}
                                {(() => {
                                    const paperTitle = getField(row, ['Paper Title', 'PaperTitle', 'Paper', 'Title']);
                                    if (paperTitle) {
                                        return (
                                            <div style={{ marginBottom: '0.4rem', fontSize: '1.05em', fontWeight: 500, lineHeight: 1.4 }}>
                                                {paperTitle}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}

                                {/* Authors, Journal, Year under Paper Title */}
                                {(() => {
                                    const journal = getField(row, ['Journal']);
                                    const year = getField(row, ['Year']);
                                    const al = getField(row, ['Author List', 'Authors', 'Author List 1', 'Author']);

                                    if (al || journal || year) {
                                        return (
                                            <div style={{ marginBottom: '0.4rem', fontSize: '0.85em', color: '#555', lineHeight: 1.5 }}>
                                                {al && (
                                                    <div style={{ marginBottom: '0.2rem' }}>
                                                        {renderAuthorsWithHighlight(al)}
                                                    </div>
                                                )}
                                                {(journal || year) && (
                                                    <div style={{ fontSize: '0.95em', color: '#666', fontStyle: 'italic' }}>
                                                        {[journal, year].filter(Boolean).join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}

                                {/* Description under title */}
                                {row['Description'] && (
                                    <div style={{ fontSize: '0.9em', color: '#666', lineHeight: 1.5 }}>
                                        {row['Description']}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Rest of content starting from left */}
                        <div>
                            {/* Secondary Paper Section */}
                            {(() => {
                                const paperTitle2 = getField(row, ['Paper Title 2', 'PaperTitle 2', 'Paper 2', 'Title 2']);
                                const journal2 = getField(row, ['Journal 2', 'Journal2']);
                                const year2 = getField(row, ['Year 2', 'Year2']);
                                const al2 = getField(row, ['Author List 2', 'Authors 2', 'Author List2', 'Author 2']);

                                if (paperTitle2 || journal2 || year2 || al2) {
                                    return (
                                        <div style={{ marginBottom: '0.8rem' }}>
                                            {paperTitle2 && (
                                                <div style={{ marginBottom: '0.4rem', fontSize: '1.05em', fontWeight: 500, lineHeight: 1.4 }}>
                                                    {paperTitle2}
                                                </div>
                                            )}
                                            {al2 && (
                                                <div style={{ marginBottom: '0.3rem', fontSize: '0.85em', color: '#555', lineHeight: 1.5 }}>
                                                    {renderAuthorsWithHighlight(al2)}
                                                </div>
                                            )}
                                            {(journal2 || year2) && (
                                                <div style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
                                                    {[journal2, year2].filter(Boolean).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            {/* Links Section */}
                            {(row['Url'] || row['GitHub'] || row['Slides'] || row['Type']) && (
                                <div style={{ marginBottom: '0', paddingTop: '0.8rem', borderTop: '1px solid #eee' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem' }}>
                                        {/* Left side: Buttons */}
                                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                                            {row['Url'] && (
                                                <a href={normalizeHref(row['Url'])} style={{
                                                    padding: '0.4rem 0.8rem',
                                                    background: '#81adb7',
                                                    borderRadius: '6px',
                                                    textDecoration: 'none',
                                                    fontSize: '0.85em',
                                                    color: '#ffffff',
                                                    fontWeight: 500,
                                                    transition: 'background 0.2s'
                                                }}>Paper</a>
                                            )}
                                            {row['GitHub'] && (
                                                <a href={normalizeHref(row['GitHub'])} style={{
                                                    padding: '0.4rem 0.8rem',
                                                    background: '#81adb7',
                                                    borderRadius: '6px',
                                                    textDecoration: 'none',
                                                    fontSize: '0.85em',
                                                    color: '#ffffff',
                                                    fontWeight: 500,
                                                    transition: 'background 0.2s'
                                                }}>Code</a>
                                            )}
                                            {row['Slides'] && (
                                                <a href={normalizeHref(row['Slides'])} style={{
                                                    padding: '0.4rem 0.8rem',
                                                    background: '#81adb7',
                                                    borderRadius: '6px',
                                                    textDecoration: 'none',
                                                    fontSize: '0.85em',
                                                    color: '#ffffff',
                                                    fontWeight: 500,
                                                    transition: 'background 0.2s'
                                                }}>Slides</a>
                                            )}
                                        </div>
                                        {/* Right side: Type Tag */}
                                        {row['Type'] && (
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '0.4rem 0.8rem',
                                                borderRadius: '6px',
                                                fontSize: '0.75em',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                background: row['Type'].toLowerCase().includes('publication') && !row['Type'].toLowerCase().includes('workshop')
                                                    ? '#E8F4F8'
                                                    : row['Type'].toLowerCase().includes('thesis')
                                                        ? '#F9E8E8'
                                                        : row['Type'].toLowerCase().includes('workshop')
                                                            ? '#EFE9F5'
                                                            : '#F8F3E8',
                                                color: row['Type'].toLowerCase().includes('publication') && !row['Type'].toLowerCase().includes('workshop')
                                                    ? '#5A8A9A'
                                                    : row['Type'].toLowerCase().includes('thesis')
                                                        ? '#9A7A7A'
                                                        : row['Type'].toLowerCase().includes('workshop')
                                                            ? '#8A7A9A'
                                                            : '#9A8A6A'
                                            }}>
                                                {row['Type']}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Additional Fields */}
                            <div>
                                {headers.map(h => {
                                    if (CATEGORY_COLUMNS.includes(h) || HIDDEN_FIELDS.has(String(h).toLowerCase())) return null;
                                    const val = (row as any)[h];
                                    if (val === undefined || val === null || String(val).trim() === '') return null;
                                    return (
                                        <div key={h} style={{ marginBottom: '0.4rem', fontSize: '0.9em', color: '#555', lineHeight: 1.5 }}>
                                            {renderValue(val)}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
};

export default ResearchCards;

